import { 
  isDateInPast, 
  isDateOverdue, 
  formatDate, 
  getStoreTimezone,
  parseDate
} from './timezone';

export interface AppointmentStatus {
  category: 'upcoming' | 'passed' | 'overdue' | 'needsAction';
  needsAction: boolean;
  isPast: boolean;
  isOverdue: boolean;
}

export interface AppointmentGroup {
  id: string;
  clientName: string;
  clientPhone: string;
  clientEmail: string;
  staffId: string;
  staffName: string;
  date: string;
  appointments: any[];
  totalPrice: number;
  totalDuration: number;
  earliestTime: string;
  latestTime: string;
  needsAction: boolean;
  hasMultipleAppointments: boolean;
  groupStatus: 'all_scheduled' | 'all_completed' | 'all_no_show';
}

export const getAppointmentStatus = (appointment: any): AppointmentStatus => {
  try {
    // Validate appointment data
    if (!appointment?.scheduledDate || typeof appointment.scheduledDate !== 'string') {
      console.warn('Invalid appointment data:', appointment);
      return {
        category: 'upcoming',
        needsAction: false,
        isPast: false,
        isOverdue: false
      };
    }
    
    const timezone = getStoreTimezone();
    const scheduledDate = parseDate(appointment.scheduledDate);
    
    const isPast = isDateInPast(scheduledDate, timezone);
    const isOverdue = isDateOverdue(scheduledDate, 0.5, timezone); // 30 minutes threshold
    
    // DATE-FIRST LOGIC:
    
    // If appointment is in the PAST
    if (isPast) {
      // Normalize status for comparison (handle different casing)
      const normalizedStatus = appointment.status?.toString().toLowerCase();
      if (normalizedStatus === 'scheduled') {
        // Past + Scheduled = Needs Action
        return {
          category: 'needsAction',
        needsAction: true,
        isPast: true,
        isOverdue: isOverdue,
      };
    } else {
      // Past + Not Scheduled = Passed
      return {
        category: 'passed',
        needsAction: false,
        isPast: true,
        isOverdue: false,
      };
    }
  } else {
    // If appointment is in the FUTURE
    // Normalize status for comparison (handle different casing)
    const normalizedStatus = appointment.status?.toString().toLowerCase();
    if (normalizedStatus === 'scheduled') {
      // Future + Scheduled = Upcoming
      return {
        category: 'upcoming',
        needsAction: false,
        isPast: false,
        isOverdue: false,
      };
    } else {
      // Future + Not Scheduled = Passed
      return {
        category: 'passed',
        needsAction: false,
        isPast: false,
        isOverdue: false,
      };
    }
  }
  } catch (error) {
    console.error('Error in getAppointmentStatus:', error);
    return {
      category: 'upcoming',
      needsAction: false,
      isPast: false,
      isOverdue: false,
    };
  }
};

export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'scheduled':
      return '#1976d2'; // Blue
    case 'show':
      return '#2e7d32'; // Green
    case 'completed':
      return '#2e7d32'; // Green (same as show)
    case 'no_show':
      return '#d32f2f'; // Red
    default:
      return '#757575'; // Grey
  }
};

export const getStatusLabel = (status: string): string => {
  // Convert to lowercase for comparison
  const normalizedStatus = status?.toLowerCase();
  
  switch (normalizedStatus) {
    case 'scheduled':
      return 'Scheduled';
    case 'show':
      return 'Completed';
    case 'completed':
      return 'Completed';
    case 'no_show':
      return 'No Show';
    default:
      return 'Unknown';
  }
};

export const categorizeAppointments = (appointments: any[]) => {
  const upcoming: any[] = [];
  const passed: any[] = [];
  const needsAction: any[] = [];
  
  appointments.forEach(appointment => {
    const status = getAppointmentStatus(appointment);
    
    if (status.needsAction) {
      needsAction.push(appointment);
    }
    
    if (status.category === 'upcoming') {
      upcoming.push(appointment);
    } else if (status.category === 'passed') {
      passed.push(appointment);
    }
  });
  
  return {
    upcoming: upcoming.sort((a, b) => {
      try {
        const dateA = parseDate(a.scheduledDate);
        const dateB = parseDate(b.scheduledDate);
        return dateA.getTime() - dateB.getTime();
      } catch (error) {
        console.warn('Error sorting upcoming appointments:', error);
        return 0;
      }
    }),
    passed: passed.sort((a, b) => {
      try {
        const dateA = parseDate(a.scheduledDate);
        const dateB = parseDate(b.scheduledDate);
        return dateB.getTime() - dateA.getTime();
      } catch (error) {
        console.warn('Error sorting passed appointments:', error);
        return 0;
      }
    }),
    needsAction: needsAction.sort((a, b) => {
      try {
        const dateA = parseDate(a.scheduledDate);
        const dateB = parseDate(b.scheduledDate);
        return dateA.getTime() - dateB.getTime();
      } catch (error) {
        console.warn('Error sorting needsAction appointments:', error);
        return 0;
      }
    }),
  };
};

export const groupAppointments = (appointments: any[]): AppointmentGroup[] => {
  const groups: { [key: string]: AppointmentGroup } = {};
  const timezone = getStoreTimezone();
  
  appointments.forEach(appointment => {
    try {
      // Skip appointments with invalid dates
      if (!appointment.scheduledDate || typeof appointment.scheduledDate !== 'string') {
        console.warn('Skipping appointment with invalid scheduledDate:', appointment);
        return;
      }
      
      const clientName = appointment.client?.name || 'Unknown Client';
      const staffId = appointment.staff?.id || 'no-staff';
      const staffName = appointment.staff?.fullName || 'Any Staff';
      const appointmentDate = parseDate(appointment.scheduledDate);
      const date = formatDate(appointmentDate, timezone);

      // Prefer DB-assigned booking group; fall back to synthetic key for legacy appointments
      const groupKey = appointment.bookingGroupId || `${clientName}-${staffId}-${date}`;
      
      if (!groups[groupKey]) {
        groups[groupKey] = {
          id: groupKey,
          clientName,
          clientPhone: appointment.client?.phone || '',
          clientEmail: appointment.client?.email || '',
          staffId,
          staffName,
          date,
          appointments: [],
          totalPrice: 0,
          totalDuration: 0,
          earliestTime: appointment.scheduledDate,
          latestTime: appointment.scheduledDate,
          needsAction: false,
          hasMultipleAppointments: false,
          groupStatus: 'all_scheduled'
        };
      }
      
      const group = groups[groupKey];
      group.appointments.push(appointment);
      group.totalPrice += parseFloat(appointment.service?.price || 0);
      group.totalDuration += parseInt(appointment.service?.duration || 0);
        // Update earliest and latest times using proper date comparison
      const appointmentTime = parseDate(appointment.scheduledDate);
      const earliestTime = parseDate(group.earliestTime);
      const latestTime = parseDate(group.latestTime);
      
      if (appointmentTime < earliestTime) {
        group.earliestTime = appointment.scheduledDate;
      }
      if (appointmentTime > latestTime) {
        group.latestTime = appointment.scheduledDate;
      }
    } catch (error) {
      console.warn('Error processing appointment:', error, appointment);
    }
  });
  
  // Process groups to determine final properties
  return Object.values(groups).map(group => {
    group.hasMultipleAppointments = group.appointments.length > 1;
    
    // Sort appointments by scheduled date to get the first one
    group.appointments.sort((a, b) => {
      const dateA = parseDate(a.scheduledDate);
      const dateB = parseDate(b.scheduledDate);
      return dateA.getTime() - dateB.getTime();
    });
    
    // Determine group status based on ALL appointments
    const allStatuses = group.appointments.map((a: any) => a.status?.toLowerCase());
    const allCompleted = allStatuses.every((s: string) => s === 'show' || s === 'completed');
    const allNoShow = allStatuses.every((s: string) => s === 'no_show');
    if (allCompleted) {
      group.groupStatus = 'all_completed';
    } else if (allNoShow) {
      group.groupStatus = 'all_no_show';
    } else {
      group.groupStatus = 'all_scheduled';
    }
    
    // Reset needsAction and recalculate based on earliest appointment
    group.needsAction = false;
    if (group.appointments.length > 0) {
      const earliestAppointment = group.appointments[0]; // Already sorted
      const appointmentStatus = getAppointmentStatus(earliestAppointment);
      group.needsAction = appointmentStatus.needsAction;
    }
    
    return group;
  });
};

export const categorizeAppointmentGroups = (appointments: any[]) => {
  const groups = groupAppointments(appointments);
  
  const upcoming: AppointmentGroup[] = [];
  const passed: AppointmentGroup[] = [];
  const needsAction: AppointmentGroup[] = [];
  
  groups.forEach(group => {
    // First priority: if group needs action, it goes to needsAction
    if (group.needsAction) {
      needsAction.push(group);
      return; // Don't categorize further
    }
    
    // Otherwise, check if group is upcoming or passed based on earliest appointment
    if (group.appointments.length > 0) {
      const earliestAppointment = group.appointments[0]; // Already sorted by date
      const status = getAppointmentStatus(earliestAppointment);
      
      if (status.category === 'upcoming') {
        upcoming.push(group);
      } else if (status.category === 'passed') {
        passed.push(group);
      } else {
        // Default to upcoming if unclear
        upcoming.push(group);
      }
    } else {
      // Default to upcoming if no appointments
      upcoming.push(group);
    }
  });
  
  return {
    upcoming: upcoming.sort((a, b) => {
      const dateA = parseDate(a.earliestTime);
      const dateB = parseDate(b.earliestTime);
      return dateA.getTime() - dateB.getTime();
    }),
    passed: passed.sort((a, b) => {
      const dateA = parseDate(a.latestTime);
      const dateB = parseDate(b.latestTime);
      return dateB.getTime() - dateA.getTime();
    }),
    needsAction: needsAction.sort((a, b) => {
      const dateA = parseDate(a.earliestTime);
      const dateB = parseDate(b.earliestTime);
      return dateA.getTime() - dateB.getTime();
    }),
  };
};
